import { useCallback, useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import {
    getMarketTypesList,
    getNetworkSearch,
    getStorageList,
} from "tm-shared/api";
import { validationMessage } from "tm-shared/constants";

import { RHFAutocomplete } from "@/components/RHF/RHFAutocomplete";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { useAsyncOptions } from "@/hooks/useAsyncOptions";
import { FieldName } from "@/pages/SV/MarketCreate/MarketCreate.constants";
import {
    setMarketCreatePageModal,
    setMarketCreatePageOptionsCache,
} from "@/store/slices/ui";
import { NewMarketFormValues } from "@/types/form";
import { MarketCreateModal } from "@/types/modals";

interface MarketTypeSectionProps {
    activeMap: Record<FieldName, boolean>;
    resetBelow: (field: FieldName) => void;
}

export const MarketTypeSection = ({
    activeMap,
    resetBelow,
}: MarketTypeSectionProps) => {
    const { getValues, setValue } = useFormContext<NewMarketFormValues>();
    const dispatch = useAppDispatch();

    const region = useWatch<NewMarketFormValues, "region">({ name: "region" });
    const network = useWatch<NewMarketFormValues, "network">({
        name: "network",
    });
    const storage = useWatch<NewMarketFormValues, "storage">({
        name: "storage",
    });
    const marketType = useWatch<NewMarketFormValues, "marketType">({
        name: "marketType",
    });
    const partner = useWatch<NewMarketFormValues, "partner">({
        name: "partner",
    });

    const { optionCache } = useAppSelector((s) => s.ui.marketCreatePage);

    const handleAddNetwork = useCallback(() => {
        dispatch(
            setMarketCreatePageModal({
                type: MarketCreateModal.AddNetwork,
            }),
        );
    }, [dispatch]);

    const { options: marketTypesOptions, loading: loadingMarketTypes } =
        useAsyncOptions({
            fetchFn: (signal) =>
                getMarketTypesList({ axiosOverrides: { signal } }),
            selectData: (response) =>
                Object.entries(response.result).filter(
                    ([value]) => value !== "--",
                ),
            mapToOption: ([value, label]) => ({ label, value }),
            dependencies: [],
            enabled: activeMap.marketType && !marketType,
        });

    useEffect(() => {
        if (marketTypesOptions?.length) {
            dispatch(
                setMarketCreatePageOptionsCache({
                    fieldName: "marketType",
                    options: marketTypesOptions,
                }),
            );
        }
    }, [dispatch, marketTypesOptions]);

    const [searchQueryChainName, setSearchQueryChainName] = useState("");

    const { options: newStorageOptions, loading: loadingStorage } =
        useAsyncOptions({
            fetchFn: (signal) => {
                const { region } = getValues();

                return getStorageList(
                    {
                        partnerId: partner,
                        mainRegionId: region,
                    },
                    {
                        axiosOverrides: { signal },
                    },
                );
            },
            selectData: (response) => response.result,
            mapToOption: (s) => ({ label: s.name, value: s.id }),
            dependencies: [region, partner],
            enabled: activeMap.storage && !storage,
        });

    useEffect(() => {
        if (newStorageOptions?.length) {
            dispatch(
                setMarketCreatePageOptionsCache({
                    fieldName: "storage",
                    options: newStorageOptions,
                }),
            );
        }
    }, [dispatch, newStorageOptions]);

    const { options: newChainNameOptions, loading: loadingChainName } =
        useAsyncOptions({
            fetchFn: (signal) =>
                getNetworkSearch(
                    {
                        query: searchQueryChainName,
                    },
                    { axiosOverrides: { signal } },
                ),
            selectData: (response) => response.result,
            mapToOption: (s) => ({ label: s.name, value: s.id }),
            dependencies: [searchQueryChainName],
            enabled:
                ((!searchQueryChainName && !network) ||
                    searchQueryChainName.length >= 3) &&
                activeMap.network,
            debounceMs: 500,
        });

    useEffect(() => {
        if (newChainNameOptions?.length) {
            dispatch(
                setMarketCreatePageOptionsCache({
                    fieldName: "network",
                    options: newChainNameOptions,
                }),
            );
        }
    }, [dispatch, newChainNameOptions]);

    const handleInputChange = useCallback(
        (value: string) => {
            setSearchQueryChainName(value);
            setValue("network", "");
        },
        [setValue],
    );

    return (
        <>
            <RHFAutocomplete
                name="marketType"
                options={optionCache?.marketType ?? []}
                label="Тип ТТ"
                allowClear={false}
                disabled={!activeMap.marketType}
                onAfterChange={() => resetBelow("marketType")}
                rules={{
                    required: validationMessage.required,
                }}
                loading={loadingMarketTypes}
                selectProps={{
                    size: "large",
                }}
            />

            {activeMap.network ? (
                <RHFAutocomplete
                    name="network"
                    options={optionCache?.network ?? []}
                    label="Назва мережі"
                    allowClear={false}
                    loading={loadingChainName}
                    onInputChange={handleInputChange}
                    rules={{
                        required: validationMessage.required,
                    }}
                    disabled={!activeMap.network}
                    actionOption={{
                        label: "Додати мережу",
                        onClick: handleAddNetwork,
                    }}
                    selectProps={{
                        size: "large",
                    }}
                />
            ) : (
                <RHFAutocomplete
                    name="storage"
                    options={optionCache?.storage ?? []}
                    label="Обслуговується зі складу"
                    disabled={!activeMap.storage}
                    onAfterChange={() => resetBelow("storage")}
                    allowClear={false}
                    loading={loadingStorage}
                    rules={{
                        required: validationMessage.required,
                    }}
                    selectProps={{
                        size: "large",
                    }}
                />
            )}
        </>
    );
};
